import { BigNumber } from "@ethersproject/bignumber";
import { parseEther } from "@ethersproject/units";

// Utils
import { getXTokenClient, getXTokenClientWithSigner } from "utils/clients";
import { formatEtherNumber } from "utils/common";
import { sentryErrorLog } from "utils/sentryLog";

// Types
import { Withdrawal } from "types/common";
import { NetworkConfigType } from "config/network/types";
import { ethConfig, polygonConfig } from "config/network";

export const getXTokenBalance = async (
  config: NetworkConfigType,
  signer: any
) => {
  const signerAddress = signer.getAddress();
  const isPolygonChainActive = config.chainId === "0x89";

  const client = getXTokenClient(
    config.contractAddresses.xtoken.token,
    config.abis.xtoken.token,
    isPolygonChainActive
  );
  const balance = await client.balanceOf(signerAddress);

  return balance;
};

export const getTVL = async () => {
  const ethClient = getXTokenClient(
    ethConfig.contractAddresses.xtoken.interactionPool,
    ethConfig.abis.xtoken.interactionPool,
    false
  );

  const polygonClient = getXTokenClient(
    polygonConfig.contractAddresses.xtoken.interactionPool,
    polygonConfig.abis.xtoken.interactionPool,
    true
  );

  const conversionPromise = ethClient.convertMaticToMaticX(parseEther("1"));
  const instantPoolMaticXonEthPromise = ethClient.instantPoolMaticX();
  const instantPoolMaticXonPolygonPromise = polygonClient.instantPoolMaticX();

  const conversionToResp = await conversionPromise;
  const conversionRate = +formatEtherNumber(conversionToResp[0].toString(), 4);
  const totalStakedonEth = +formatEtherNumber(
    conversionToResp[2].toString(),
    0
  );

  const instantPoolMaticXonEthResp = await instantPoolMaticXonEthPromise;
  const instantPoolMaticXonEth = +formatEtherNumber(
    instantPoolMaticXonEthResp,
    0
  );

  const instantPoolMaticXonMaticResp = await instantPoolMaticXonPolygonPromise;
  const instantPoolMaticXonPolygon = +formatEtherNumber(
    instantPoolMaticXonMaticResp,
    0
  );

  return +(
    totalStakedonEth -
    instantPoolMaticXonEth * conversionRate -
    instantPoolMaticXonPolygon * conversionRate
  ).toFixed(0);
};

export const getInstantPoolMaticX = async (config: NetworkConfigType) => {
  const isPolygonChainActive = config.chainId === "0x89";

  const client = getXTokenClient(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    isPolygonChainActive
  );

  const instantPool = await client.instantPoolMaticX();

  return instantPool;
};

export const getInstantPoolMatic = async (config: NetworkConfigType) => {
  const isPolygonChainActive = config.chainId === "0x89";

  const client = getXTokenClient(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    isPolygonChainActive
  );

  const instantPool = await client.instantPoolMatic();

  return instantPool;
};

export const submitTransaction = async (
  config: NetworkConfigType,
  signer: any,
  amount: string,
  useLowGas: boolean,
  isPolygonChainActive: boolean
) => {
  let txn;
  const client = await getXTokenClientWithSigner(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    signer
  );

  if (useLowGas || isPolygonChainActive) {
    const amt = isPolygonChainActive
      ? {
          value: parseEther(amount),
        }
      : parseEther(amount);

    txn = await client.swapMaticForMaticXViaInstantPool(amt);
  } else {
    txn = await client.submit(parseEther(amount));
  }

  return txn;
};

export const getAllowanceMaticX = async (signer: any) => {
  const signerAddress = await signer.getAddress();

  const client = getXTokenClient(
    polygonConfig.contractAddresses.xtoken.token,
    polygonConfig.abis.xtoken.token,
    true
  );

  const allowance = await client.allowance(
    signerAddress,
    polygonConfig.contractAddresses.xtoken.interactionPool
  );

  return allowance;
};

export const addApprovalMaticX = async (
  config: NetworkConfigType,
  signer: any,
  amount: any,
  isIncrement: boolean
) => {
  const client = await getXTokenClientWithSigner(
    config.contractAddresses.xtoken.token,
    config.abis.xtoken.token,
    signer
  );

  let txn;

  if (isIncrement) {
    txn = await client.increaseAllowance(
      config.contractAddresses.xtoken.interactionPool,
      amount
    );
  } else {
    txn = await client.approve(
      config.contractAddresses.xtoken.interactionPool,
      amount
    );
  }

  return txn;
};

export const requestWithdraw = async (
  config: NetworkConfigType,
  signer: any,
  amount: any,
  isPolygonChainActive: boolean
) => {
  const client = await getXTokenClientWithSigner(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    signer
  );

  let txn;

  if (isPolygonChainActive) {
    txn = await client.requestMaticXSwap(parseEther(amount));
  } else {
    txn = await client.requestWithdraw(parseEther(amount));
  }

  return txn;
};

export const getWithdrawalRequests = async (
  config: NetworkConfigType,
  signer: any
) => {
  const signerAddress = signer.getAddress();
  const isPolygonChainActive = config.chainId === "0x89";

  const client = getXTokenClient(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    isPolygonChainActive
  );

  let response;
  let formattedResponse;

  if (isPolygonChainActive) {
    response = await client.getUserMaticXSwapRequests(signerAddress);

    formattedResponse = response.map(
      (withdrawals: Withdrawal, index: number) => ({
        amount: +formatEtherNumber(withdrawals.amount.toString(), 4),
        requestTime: withdrawals.requestTime.toString(),
        withdrawalTime: withdrawals.withdrawalTime.toString(),
        id: index,
      })
    );
  } else {
    response = await client.getUserWithdrawalRequests(signerAddress);

    formattedResponse = await Promise.all<Withdrawal>(
      response.map(async (withdrawal: Withdrawal, index: number) => {
        return client
          .getSharesAmountOfUserWithdrawalRequest(signerAddress, index)
          .then((value: BigNumber) => ({
            validatorNonce: withdrawal.validatorNonce
              ? +withdrawal.validatorNonce.toString()
              : 0,
            requestEpoch: withdrawal.requestEpoch
              ? +withdrawal.requestEpoch
              : 0,
            amount: +formatEtherNumber(value.toString(), 4),
            id: index,
          }));
      })
    );
  }

  return formattedResponse;
};

export const addXTokenToWallet = async (
  config: NetworkConfigType,
  provider: any
) => {
  await provider.sendAsync(
    {
      method: "wallet_watchAsset",
      params: {
        type: "ERC20",
        options: {
          address: config.contractAddresses.xtoken.token,
          symbol: config.nativeCurrency.xsymbol,
          decimals: config.nativeCurrency.decimals,
        },
      },
    },
    (error: any) => {
      sentryErrorLog(error);
    }
  );
};

export const getToConversion = async (
  config: NetworkConfigType,
  amount: any
) => {
  try {
    const isPolygonChainActive = config.chainId === "0x89";

    const client = getXTokenClient(
      config.contractAddresses.xtoken.interactionPool,
      config.abis.xtoken.interactionPool,
      isPolygonChainActive
    );
    const resp = await client.convertMaticXToMatic(amount.toString());
    return resp;
  } catch (error) {
    sentryErrorLog(error);
  }
};

export const getFromConversion = async (
  config: NetworkConfigType,
  amount: any
) => {
  try {
    const isPolygonChainActive = config.chainId === "0x89";

    const client = getXTokenClient(
      config.contractAddresses.xtoken.interactionPool,
      config.abis.xtoken.interactionPool,
      isPolygonChainActive
    );
    const resp = await client.convertMaticToMaticX(amount.toString());
    return resp;
  } catch (error) {
    sentryErrorLog(error);
  }
};

export const claimWithdrawal = async (
  config: NetworkConfigType,
  signer: any,
  id: number,
  isPolygonChainActive: boolean
) => {
  const client = await getXTokenClientWithSigner(
    config.contractAddresses.xtoken.interactionPool,
    config.abis.xtoken.interactionPool,
    signer
  );
  let txn;

  if (isPolygonChainActive) {
    txn = await client.claimMaticXSwap(id);
  } else {
    txn = await client.claimWithdrawal(id);
  }

  return txn;
};

export const getStakingLimitDetails = async (config: NetworkConfigType) => {
  try {
    const isPolygonChainActive = config.chainId === "0x89";

    const client = getXTokenClient(
      config.contractAddresses.xtoken.interactionPool,
      config.abis.xtoken.interactionPool,
      isPolygonChainActive
    );
    const [instantPoolMaticX, instantPoolMatic] = await Promise.all([
      client.instantPoolMaticX(),
      client.instantPoolMatic(),
    ]);

    const connvertedMaticXtoMatic = await client.convertMaticXToMatic(
      instantPoolMaticX
    );

    return {
      totalLimit:
        +formatEtherNumber(instantPoolMatic.toString(), 0) +
        +formatEtherNumber(connvertedMaticXtoMatic[0].toString(), 0),
      exhaustedLimit: +formatEtherNumber(instantPoolMatic, 0),
    };
  } catch (error) {
    sentryErrorLog(error);
  }
};
